import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Badge, SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

function isToday(dateStr) {
  return dateStr?.split('T')[0] === new Date().toISOString().split('T')[0];
}
function isPast(dateStr) {
  return dateStr && new Date(dateStr) < new Date(new Date().toDateString());
}

export default function StudentTimetablePage() {
  const { addToast } = useToast();
  const [timetable, setTimetable] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [cohortInfo, setCohortInfo] = useState(null);

  useEffect(() => {
    Promise.all([
      API.get('/timetable/my-timetable').then(r => r.data),
      API.get('/cohorts/my-cohort').then(r => r.data).catch(() => ({ cohorts:[] })),
    ]).then(([tt, cohortData]) => {
      setTimetable(tt.timetable || []);
      if (cohortData.cohorts?.length) setCohortInfo(cohortData.cohorts[0]);
    }).catch(() => addToast('Failed to load timetable', 'error'))
      .finally(() => setLoading(false));
  }, []);

  // Group by week
  const byWeek = timetable.reduce((acc, t) => {
    const w = t.week_number;
    if (!acc[w]) acc[w] = [];
    acc[w].push(t);
    return acc;
  }, {});

  // Find current week
  const today = new Date();
  const currentWeekEntries = Object.entries(byWeek).find(([,entries]) =>
    entries.some(e => {
      const d = new Date(e.date);
      const weekStart = new Date(d); weekStart.setDate(d.getDate() - d.getDay());
      const weekEnd   = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
      return today >= weekStart && today <= weekEnd;
    })
  );
  const currentWeek = currentWeekEntries ? parseInt(currentWeekEntries[0]) : null;

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" className="text-brand-500"/></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <SectionHeader title="Timetable" subtitle="Your weekly class schedule"/>

      {/* Cohort info */}
      {cohortInfo && (
        <div className="mb-6 p-4 bg-de-black text-white rounded-2xl flex items-center justify-between gap-4">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Your Cohort</p>
            <p className="font-bold text-base">{cohortInfo.name}</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {Array.isArray(cohortInfo.days) ? cohortInfo.days.join(', ') : cohortInfo.days}
              {' · '}{cohortInfo.start_time}–{cohortInfo.end_time}
            </p>
          </div>
          {cohortInfo.meet_link && (
            <a href={cohortInfo.meet_link} target="_blank" rel="noopener noreferrer"
              className="flex-shrink-0 px-4 py-2 bg-brand-500 text-white rounded-xl text-sm font-bold hover:bg-brand-600 transition-colors">
              🔗 Join Class
            </a>
          )}
        </div>
      )}

      {timetable.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-4xl mb-3">📅</p>
          <p className="font-bold text-de-black mb-1">No timetable yet</p>
          <p className="text-sm text-gray-500">Your teacher hasn't set up the timetable yet. Check back soon!</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.keys(byWeek).sort((a,b) => +a - +b).map(week => {
            const isCurrentWeek = parseInt(week) === currentWeek;
            return (
              <div key={week}>
                <div className={`flex items-center gap-2 mb-2 px-1`}>
                  <p className={`text-sm font-bold ${isCurrentWeek ? 'text-brand-500' : 'text-gray-500'}`}>
                    Week {week}
                  </p>
                  {isCurrentWeek && (
                    <span className="px-2 py-0.5 bg-brand-500 text-white rounded-full text-xs font-bold">Current</span>
                  )}
                </div>
                <Card className={`overflow-hidden ${isCurrentWeek ? 'border-brand-300 border-2' : ''}`}>
                  {byWeek[week].map((entry, i) => {
                    const past    = isPast(entry.date);
                    const today_  = isToday(entry.date);
                    const meetLink = entry.meet_link || cohortInfo?.meet_link;
                    return (
                      <div key={entry.id}
                        className={`flex items-center justify-between px-5 py-3.5
                          ${i > 0 ? 'border-t border-gray-100' : ''}
                          ${today_ ? 'bg-brand-50' : past ? 'bg-gray-50' : 'bg-white'}`}>
                        <div className="flex items-center gap-4">
                          <div className={`text-center w-14 ${past ? 'opacity-50' : ''}`}>
                            <p className={`text-xs font-bold ${today_ ? 'text-brand-500' : 'text-gray-600'}`}>
                              {entry.day?.slice(0,3)}
                            </p>
                            <p className="text-xs text-gray-400">
                              {entry.date ? new Date(entry.date).toLocaleDateString('en-GB',{day:'numeric',month:'short'}) : ''}
                            </p>
                          </div>
                          <div>
                            <p className={`text-sm font-semibold ${past ? 'text-gray-400' : 'text-de-black'}`}>
                              {entry.topic}
                            </p>
                            <p className="text-xs text-gray-400">{entry.start_time || cohortInfo?.start_time} – {entry.end_time || cohortInfo?.end_time}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {today_ && <span className="px-2 py-0.5 bg-brand-500 text-white rounded-full text-xs font-bold">Today!</span>}
                          {past && <span className="text-xs text-gray-400">Done</span>}
                          {meetLink && !past && (
                            <a href={meetLink} target="_blank" rel="noopener noreferrer"
                              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors
                                ${today_ ? 'bg-brand-500 text-white hover:bg-brand-600' : 'bg-surface border border-gray-200 text-gray-700 hover:border-brand-300'}`}>
                              🔗 {today_ ? 'Join Now' : 'Meet'}
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </Card>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
